import {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    formatDateHM,
    formatDateHMRange,
    formatFullDMY
} from 'crqlar-react-common/dist/utils/date.util';
import {useLocalize} from 'crqlar-react-common/dist/utils/text.util';
import {useAllAgeCategories} from 'crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories';
import {useReadStore} from './useReadStore';
import {
    getFloorplanGuestCount,
    sortByStartDate,
} from '../utils/reservation-utils';
import {useTimeframe} from './useTimeframe';
import {RootState} from '../../../../../store/hooks';
import {resetActionFloorplanPrint} from '../../../../../store/action.reducer';
import {useFloorplanContext} from "./useFloorplanContext";
import {
    buildPrintReservationRows,
    Orientation,
    PrintLabels,
    PrintReservationRow,
    captureCanvasDataUrl,
    escapeHtml,
    filterByTimeframe,
} from '../utils/print-utils';
import {StoredTableGroup} from "../utils/table-groups";

export type PrintPreviewData = {
    dataUrl: string;
    imageWidth: number;
    imageHeight: number;
    orientation: Orientation;
    printTitle: string;
    formattedDate: string;
    timeInfo: string;
    totalReservations: number;
    totalGuests: number;
    timeframeRange: string;
    reservationRows: PrintReservationRow[];
    labels: PrintLabels;
    restaurantNameEscaped: string;
};

const pad = (value: number) => String(value).padStart(2, '0');

const formatPrintDate = (date: Date) =>
    `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;

const formatPrintTime = (date: Date) =>
    `${pad(date.getHours())}:${pad(date.getMinutes())}`;

const sanitizeFileNameSegment = (value: string) =>
    value
        .trim()
        .replace(/[\\/:*?"<>|]+/g, '_')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '') || 'Restaurant';

/**
 * Listens for the Redux floorplan print trigger, captures a canvas snapshot,
 * and opens a preview dialog. The caller renders the dialog using the returned state.
 */
export function useFloorplanPrint(tableGroups: StoredTableGroup[] = []) {
    const {canvas, restaurantName, showSeatsAroundTable} = useFloorplanContext();
    const {t} = useLocalize();
    const ageCategories = useAllAgeCategories();
    const {date, timeframeStart, timeframeEnd, referenceTime, isRange, reservationDaySlice} = useTimeframe();

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState<PrintPreviewData | null>(null);

    const openPreview = useCallback(async (orientation: Orientation) => {
        if (!canvas) return;
        const objects = canvas.getObjects();
        if (!objects.length) return;

        const capturedImage = await captureCanvasDataUrl(
            canvas,
            {timeframeStart, timeframeEnd, referenceTime, showSeatsAroundTable},
            tableGroups,
        );

        const {assignedReservations} = useReadStore.getState();

        // Match Timeframe.tsx label format: "sliceLabel: HH:MM" or "sliceLabel: HH:MM – HH:MM"
        const sliceKey = reservationDaySlice || 'now';
        const sliceLabel = t(`Day part restaurant label ${sliceKey}`);
        const timeInfo = !timeframeStart || !timeframeEnd
            ? sliceLabel
            : `${sliceLabel}: ${isRange ? formatDateHMRange(timeframeStart, timeframeEnd) : formatDateHM(timeframeStart)}`;

        const sortedReservations = sortByStartDate(
            filterByTimeframe(assignedReservations, timeframeStart, timeframeEnd),
        );

        const exportTime = new Date();
        const printTitle = `Floorplan_${sanitizeFileNameSegment(restaurantName)}_${formatPrintDate(date)}_${formatPrintTime(exportTime)}`;

        const totalGuests = sortedReservations.reduce(
            (sum, reservation) => sum + getFloorplanGuestCount(reservation), 0,
        );

        const firstStart = sortedReservations.find(
            r => r.restaurantReservation?.expectedStartDate,
        )?.restaurantReservation?.expectedStartDate;
        const lastEnd = [...sortedReservations]
            .reverse()
            .find(r => r.restaurantReservation?.expectedEndDate)
            ?.restaurantReservation?.expectedEndDate;
        const timeframeRange = firstStart && lastEnd
            ? formatDateHMRange(new Date(firstStart), new Date(lastEnd))
            : '';

        const reservationRows = buildPrintReservationRows(sortedReservations, t, date, ageCategories);

        const labels: PrintLabels = {
            floorplan: t('Floorplan'),
            date: t('Date'),
            time: t('Time'),
            todaysReservations: t("Today's reservations"),
            todaysGuests: t("Today's guests"),
            timeframe: t('Timeframe'),
            guestRoom: `${t('Guest')} / ${t('Room')}`,
            guest: t('Guest'),
            status: t('Status'),
            nationality: t('Nationality'),
            stayCount: t('Stay count'),
            allergiesDiets: t('Allergies and Diets'),
            table: t('Table'),
            start: t('Start'),
            end: t('End'),
            // "guests" carries the adult/child split (4 (2/2)); "total" is the plain sum, so it
            // shares the label every reservation list uses for exactly that number.
            guests: t('Guests'),
            total: t('Guest count abbreviation'),
            ageCategories: t('Age cat.'),
            aliasAgeCategories: `${t('Age cat.')} (${t('Alias')})`,
            notes: t('Notes'),
            stay: t('Stay'),
            room: t('Room'),
            roomNumber: t('Room number'),
            rateCode: t('Rate code'),
        };

        setPreviewData({
            dataUrl: capturedImage.dataUrl,
            imageWidth: capturedImage.width,
            imageHeight: capturedImage.height,
            orientation,
            printTitle,
            formattedDate: formatFullDMY(date),
            timeInfo,
            totalReservations: sortedReservations.length,
            totalGuests,
            timeframeRange,
            reservationRows,
            labels,
            restaurantNameEscaped: escapeHtml(restaurantName),
        });
        setPreviewOpen(true);
    }, [canvas, restaurantName, t, date, timeframeStart, timeframeEnd, referenceTime, showSeatsAroundTable, isRange, reservationDaySlice, tableGroups, ageCategories]);

    const cancelPreview = useCallback(() => {
        setPreviewOpen(false);
    }, []);

    // Redux print trigger (dispatched directly from the reservation sidebar)
    const floorplanPrintTriggered = useSelector(
        (state: RootState) => state.action.floorplanPrintTriggered,
    );
    const dispatch = useDispatch();

    useEffect(() => {
        if (!floorplanPrintTriggered) return;
        openPreview('portrait');
        dispatch(resetActionFloorplanPrint());
    }, [floorplanPrintTriggered, dispatch, openPreview]);

    return {previewOpen, previewData, cancelPreview};
}
